import { createElement, useLayoutEffect, useRef, useState } from 'react';

// Scroll reveal for the marketing pages: a section or card fades in and rises 20px the first time
// it scrolls into view, once only. One IntersectionObserver serves the whole page.
//
// Content is never hidden unless we are sure it will come back:
//   - the first render is fully visible (so no-JS / first paint never hides anything);
//   - anything on screen at load, reduced motion, or no IntersectionObserver: left as is;
//   - only content below the fold is hidden, before the first paint (useLayoutEffect);
//   - it is shown when it scrolls in, when focus moves into it, before printing, or after
//     1.5s if the observer never reports on it at all.
// Only opacity and transform change, so nothing moves the layout.

const DURATION = 350;   // ms
const STAGGER = 80;     // ms between cards that come into view together
const MAX_STAGGER = 3;  // the 4th card and later share the 4th card's delay
const SAFETY_MS = 1500;
const COUNT_MS = 800;

const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const canObserve = () => typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';

const watched = new Map(); // element -> { stagger, done(delay), seen, cleanup }
let observer = null;

function getObserver() {
  if (observer) return observer;
  observer = new window.IntersectionObserver(entries => {
    const perParent = new Map();
    for (const e of entries) {
      const w = watched.get(e.target);
      if (!w) continue;
      w.seen = true;
      // Above the viewport (jumped past it): show it now, there is nothing to animate.
      if (!e.isIntersecting && e.boundingClientRect.bottom > 0) continue;
      let delay = 0;
      if (w.stagger && e.isIntersecting) {
        const n = perParent.get(e.target.parentElement) || 0;
        perParent.set(e.target.parentElement, n + 1);
        delay = Math.min(n, MAX_STAGGER) * STAGGER;
      }
      w.done(delay);
    }
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
  window.addEventListener('beforeprint', () => [...watched.values()].forEach(w => w.done(0)));
  return observer;
}

function unwatch(el) {
  const w = watched.get(el);
  if (!w) return;
  watched.delete(el);
  w.cleanup();
  if (observer) observer.unobserve(el);
}

// Calls onShow(delayMs) once, when el should appear. Returns a cleanup function.
function watch(el, { stagger = false, onShow }) {
  const w = { stagger, seen: false };
  w.done = delay => { if (watched.get(el) !== w) return; unwatch(el); onShow(delay); };
  const timer = setTimeout(() => { if (!w.seen) w.done(0); }, SAFETY_MS);
  const onFocus = () => w.done(0);
  el.addEventListener('focusin', onFocus);
  w.cleanup = () => { clearTimeout(timer); el.removeEventListener('focusin', onFocus); };
  watched.set(el, w);
  getObserver().observe(el);
  return () => unwatch(el);
}

// True when el is below the fold at first paint and motion is allowed -- the only case we hide.
// The fold is the layout viewport height: phones can report a larger innerHeight while the page
// is still zoomed out during its first layout.
function shouldHide(el) {
  if (!el || reducedMotion() || !canObserve()) return false;
  return el.getBoundingClientRect().top >= (document.documentElement.clientHeight || window.innerHeight);
}

// [ref, style] -- spread style onto the element (undefined when nothing changes).
export function useReveal({ stagger = false } = {}) {
  const ref = useRef(null);
  const [state, setState] = useState({ phase: 'static', delay: 0 });
  useLayoutEffect(() => {
    if (!shouldHide(ref.current)) return undefined;
    setState({ phase: 'hidden', delay: 0 });
    return watch(ref.current, { stagger, onShow: delay => setState({ phase: 'shown', delay }) });
  }, [stagger]);
  if (state.phase === 'static') return [ref, undefined];
  if (state.phase === 'hidden') return [ref, { opacity: 0, transform: 'translateY(20px)' }];
  const t = `${DURATION}ms ease-out ${state.delay}ms`;
  return [ref, { opacity: 1, transform: 'none', transition: `opacity ${t}, transform ${t}` }];
}

// Renders `as` (default div) with the reveal applied. stagger: cards in a row that come into
// view together appear 80ms apart.
export default function Reveal({ as = 'div', stagger = false, style, children, ...rest }) {
  const [ref, reveal] = useReveal({ stagger });
  return createElement(as, { ...rest, ref, 'data-reveal': '', style: reveal ? { ...style, ...reveal } : style }, children);
}

// A stat figure such as "88,000+", "2.1M+" or "40+" that counts up from 0 once it scrolls into
// view. The final text is always in the DOM (and keeps the width); the moving number is an
// aria-hidden overlay on top of it. Anything that does not start with a number renders as is.
export function CountUp({ value }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(null); // null: the final value is showing
  const m = /^(\D*?)(\d[\d,]*(?:\.\d+)?)(.*)$/.exec(String(value));
  useLayoutEffect(() => {
    if (!m || !shouldHide(ref.current)) return undefined;
    const [, prefix, num, suffix] = m;
    const target = parseFloat(num.replace(/,/g, ''));
    const decimals = (num.split('.')[1] || '').length;
    const fmt = n => prefix + n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: num.includes(',') }) + suffix;
    let raf = 0;
    setShown(fmt(0));
    const stop = watch(ref.current, {
      onShow: () => {
        const t0 = performance.now();
        const tick = now => {
          const p = Math.min(1, (now - t0) / COUNT_MS);
          if (p >= 1) { setShown(null); return; }
          setShown(fmt(target * (1 - Math.pow(1 - p, 3))));
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
    });
    return () => { stop(); cancelAnimationFrame(raf); };
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <span ref={ref} data-countup={value} style={{ position: 'relative', display: 'inline-block' }}>
      <span style={shown === null ? undefined : { color: 'transparent' }}>{value}</span>
      {shown !== null && (
        <span aria-hidden="true" style={{ position: 'absolute', inset: 0, textAlign: 'center', whiteSpace: 'nowrap' }}>{shown}</span>
      )}
    </span>
  );
}
